import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  Check,
  Phone,
  MessageSquare,
  TrendingUp,
  ShieldCheck,
  Zap,
  Crown,
  CreditCard,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

/**
 * The conversion modal — make the upgrade feel like an absolute no-brainer.
 * Shown when a free-tier user clicks any "Contacter ce parent" or "Débloquer".
 *
 * ALL upgrades now route to the white-label Nucleus Pay checkout
 * (/nucleus-pay/checkout) — card (Stripe), Mobile Money (pawaPay) and bank
 * transfer all happen inside Nucleus Pay. No external Stripe redirect, no
 * manual Mobile Money instructions.
 */
export const ParentLeadsUpgradeModal = ({ open, onClose, defaultCycle = 'yearly' }) => {
  const { t, language } = useLanguage();
  const [billingCycle, setBillingCycle] = useState(defaultCycle); // default = yearly

  // Sync if parent toggles defaultCycle
  React.useEffect(() => { if (defaultCycle) setBillingCycle(defaultCycle); }, [defaultCycle, open]);

  const isFr = language !== 'en';

  const goToNucleusPay = () => {
    const purpose = billingCycle === 'yearly' ? 'partner_yearly' : 'partner_monthly';
    onClose();
    // Use plain navigation: UpgradeProvider is mounted ABOVE the BrowserRouter,
    // so useNavigate() is not available here. window.location keeps SPA behaviour
    // consistent on the target route which IS inside the Router.
    window.location.assign(`/nucleus-pay/checkout?purpose=${purpose}`);
  };

  const benefits = [
    {
      icon: Phone,
      title: t('parentLeads.benefit_1_title'),
      desc: t('parentLeads.benefit_1_desc'),
    },
    {
      icon: MessageSquare,
      title: t('parentLeads.benefit_2_title'),
      desc: t('parentLeads.benefit_2_desc'),
    },
    {
      icon: TrendingUp,
      title: t('parentLeads.benefit_3_title'),
      desc: t('parentLeads.benefit_3_desc'),
    },
    {
      icon: ShieldCheck,
      title: t('parentLeads.benefit_4_title'),
      desc: t('parentLeads.benefit_4_desc'),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="parent-leads-upgrade-modal"
        className="sm:max-w-2xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-rose-600 via-orange-600 to-amber-500 text-white px-6 py-7">
          <div className="absolute -top-20 -right-10 w-72 h-72 bg-white/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/15 ring-1 ring-white/30 text-[11px] font-bold uppercase tracking-wider mb-3">
              <Crown className="w-3 h-3" /> {t('parentLeads.modal_pill')}
            </div>
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold leading-tight">
                {t('parentLeads.modal_headline')}
              </DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-orange-50 text-sm md:text-base leading-relaxed">
              {t('parentLeads.modal_body_1')}
            </p>
            <p className="mt-2 text-white font-semibold text-sm md:text-base">
              {t('parentLeads.modal_body_2')}
            </p>
          </div>
        </div>

        {/* Plan card */}
        <div className="px-6 py-6 space-y-5 bg-white">
          {/* Billing cycle toggle — annual default */}
          <div
            data-testid="billing-toggle"
            className="grid grid-cols-2 gap-2 p-1 rounded-full bg-slate-100"
          >
            <button
              type="button"
              onClick={() => setBillingCycle('yearly')}
              data-testid="billing-yearly"
              className={`relative py-2 rounded-full text-sm font-bold transition-all ${
                billingCycle === 'yearly'
                  ? 'bg-white shadow-md text-rose-700'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {isFr ? 'Annuel' : 'Yearly'}
              <span className="absolute -top-2 right-2 px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider">
                {isFr ? '5 mois offerts' : '5 mo free'}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setBillingCycle('monthly')}
              data-testid="billing-monthly"
              className={`py-2 rounded-full text-sm font-bold transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white shadow-md text-rose-700'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {isFr ? 'Mensuel' : 'Monthly'}
            </button>
          </div>

          <div className="rounded-2xl border-2 border-orange-300 bg-gradient-to-br from-orange-50 to-rose-50 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-1">
                  {billingCycle === 'yearly'
                    ? (isFr ? 'Cycle complet · Programmes UB' : 'Full cycle · UB programs')
                    : (isFr ? 'Formule pilote' : 'Pilot tier')}
                </p>
                <h3 className="font-display text-xl font-extrabold text-oxford-800">
                  {billingCycle === 'yearly'
                    ? (isFr ? "Accès Partenaire annuel" : 'Annual Partner access')
                    : (isFr ? 'Accès Partenaire mensuel' : 'Monthly Partner access')}
                </h3>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-baseline gap-1 justify-end">
                  <span className="text-3xl font-extrabold text-oxford-800">
                    {billingCycle === 'yearly' ? 290 : 49}
                  </span>
                  <span className="text-lg font-bold text-oxford-700">€</span>
                </div>
                <p className="text-xs text-slate-600 font-medium">
                  / {billingCycle === 'yearly' ? (isFr ? 'an' : 'year') : (isFr ? 'mois' : 'month')}
                </p>
                {billingCycle === 'yearly' ? (
                  <p className="text-[10px] text-emerald-700 font-bold">
                    {isFr ? '≈ 24,17 €/mois — 5 mois offerts' : '≈ €24.17/mo — 5 months free'}
                  </p>
                ) : (
                  <p className="text-[10px] text-slate-500">
                    {isFr ? 'Sans engagement · 5 contacts/mois' : 'No commitment · 5 contacts/mo'}
                  </p>
                )}
              </div>
            </div>

            <ul className="space-y-2.5">
              {(billingCycle === 'yearly'
                ? [
                    { icon: Phone, title: isFr ? '30 contacts familles réels / mois' : '30 real family contacts / mo',
                      desc: isFr ? 'WhatsApp, téléphone, email — débloqués sur acceptation' : 'WhatsApp, phone, email — unlocked on accept' },
                    { icon: Crown, title: isFr ? 'Éligibilité subventions UB' : 'UB grants eligibility',
                      desc: isFr ? 'Candidatez au programme Modernisation 873 000 €' : 'Apply to the €873k Modernisation program' },
                    { icon: TrendingUp, title: isFr ? 'Routage prioritaire des familles' : 'Priority family routing',
                      desc: isFr ? 'Vos profils remontent en premier dans la zone' : 'Your school surfaces first locally' },
                    { icon: ShieldCheck, title: isFr ? 'Statut institution active 2026' : 'Active institution 2026',
                      desc: isFr ? 'Pleine participation au cycle annuel UB' : 'Full participation in UB annual cycle' },
                  ]
                : [
                    { icon: Phone, title: isFr ? '5 contacts familles réels / mois' : '5 real family contacts / mo',
                      desc: isFr ? 'Débloquez les WhatsApp & emails après acceptation' : 'Unlock WhatsApp & emails on accept' },
                    { icon: MessageSquare, title: isFr ? 'Messagerie illimitée avec parents' : 'Unlimited parent messaging',
                      desc: isFr ? 'Conversation complète sans limite de réponses' : 'Full chat — no reply limit' },
                    { icon: Check, title: isFr ? 'Réception de candidatures officielles' : 'Receive official applications',
                      desc: isFr ? 'Acceptez les inscriptions dans votre Bureau' : 'Accept enrollments from your Admissions Office' },
                    { icon: ShieldCheck, title: isFr ? 'Idéal pour tester en pilote' : 'Perfect for pilot testing',
                      desc: isFr ? 'Pas d\'engagement annuel, basculez à l\'annuel quand prêt' : 'No annual commitment — upgrade anytime' },
                  ]
              ).map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="grid place-items-center flex-shrink-0 w-7 h-7 rounded-lg bg-rose-600/10 text-rose-700 mt-0.5">
                    <b.icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-oxford-800 leading-tight">
                      {b.title}
                    </p>
                    <p className="text-xs text-slate-600 leading-snug">{b.desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            {billingCycle === 'monthly' && (
              <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-[11px] text-amber-900">
                <strong>{isFr ? 'Bon à savoir :' : 'Good to know:'}</strong>{' '}
                {isFr
                  ? 'Si vous basculez à l\'annuel plus tard, votre période mensuelle restante est conservée et l\'année est ajoutée par-dessus.'
                  : 'Switching to yearly later keeps your remaining monthly days — the year is added on top.'}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-orange-200/70 flex items-center gap-2 text-xs text-slate-600">
              <Zap className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
              <span>{t('parentLeads.roi_line')}</span>
            </div>
          </div>

          {/* CTAs — single white-label Nucleus Pay entry point */}
          <div className="flex flex-col gap-2.5">
            <Button
              onClick={goToNucleusPay}
              data-testid="parent-leads-stripe-cta"
              className="w-full bg-gradient-to-r from-rose-600 to-orange-600 hover:from-rose-700 hover:to-orange-700 text-white font-bold py-6 rounded-xl text-base shadow-lg hover:scale-[1.01] transition-all"
            >
              <CreditCard className="w-5 h-5 mr-2" />
              {billingCycle === 'yearly'
                ? (isFr ? 'Payer via Nucleus Pay · 290 €/an' : 'Pay via Nucleus Pay · €290/yr')
                : (isFr ? 'Payer via Nucleus Pay · 49 €/mois' : 'Pay via Nucleus Pay · €49/mo')}
            </Button>
            <p className="text-center text-[11px] text-slate-500">
              {isFr
                ? 'Carte bancaire, Mobile Money (Orange · MTN · Wave · Moov) ou virement — tout est sécurisé dans Nucleus Pay, sans quitter la plateforme.'
                : 'Card, Mobile Money or bank transfer — all secured inside Nucleus Pay, no redirection.'}
            </p>

            <button
              onClick={onClose}
              data-testid="parent-leads-modal-close"
              className="text-sm text-slate-500 hover:text-slate-700 font-medium pt-1"
            >
              {t('parentLeads.cta_later')}
            </button>
          </div>

          <div className="flex items-center justify-center gap-2 text-xs text-slate-500 pt-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>{t('parentLeads.guarantee')}</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ParentLeadsUpgradeModal;
